import React from "react"
import { SelectChangeEvent, SelectProps } from "@mui/material"
import { SelectComponentItem } from "../../ui/inputs"
import { REVIEW_BADGE_ORIGIN } from "../../ui/badges"
import { onKeyboardSubmit } from "../../helpers"
import { SELECT_ITEM_ATTRIBUTES } from "../../constants"
import { StyledReviewBadge, StyledSelect } from "./styles"
import {
  TooltipFeedbackGroup,
  TooltipFeedbackGroupProps
} from "../../feedback/TooltipFeedbackGroup"
import { TooltipFeedback } from "../../feedback/TooltipFeedback"
import { ContainerType, Variant } from "../../types"

export type InlineChoiceProps = Omit<SelectProps, "onChange"> & {
  onChange: (value: string, event?: SelectChangeEvent<any>) => void
  correct?: boolean
  correctAnswer?: string
  groupMode?: boolean
  feedbackItems?: TooltipFeedbackGroupProps["items"]
  badgeClass?: { variant: Variant | null }
  containerType?: ContainerType
  questionCompleted?: boolean
}

const renderSelectValue =
  (choiceElements: React.ReactElement[]) =>
  (value: unknown): React.ReactNode => {
    const currentElement = choiceElements.find(
      inlineChoice => inlineChoice.props.identifier === value
    )
    return currentElement ?? <>{value}</>
  }

export const InlineChoice: React.FC<InlineChoiceProps> = props => {
  const {
    children,
    value,
    onChange,
    correct,
    correctAnswer,
    groupMode = false,
    feedbackItems,
    badgeClass,
    containerType,
    questionCompleted,
    ...selectProps
  } = props
  const isLesson = containerType !== "assessment"

  const handleChange = (event: SelectChangeEvent<any>) => {
    onChange(event.target.value, event)
  }

  const handleKeyDown = (value: string) => () => onChange(value)

  const childrenArr = React.Children.toArray(children).filter(
    (child: any) => child.props
  ) as React.ReactElement[]
  const transformedChildren = childrenArr.map(
    child =>
      child?.props?.identifier && (
        <SelectComponentItem
          key={child.props.identifier}
          value={child.props.identifier}
          onKeyDown={onKeyboardSubmit(handleKeyDown(child.props.identifier))}
          {...SELECT_ITEM_ATTRIBUTES}
          {...child.props?.accessibilityAttr}
        >
          {child.props.children}
        </SelectComponentItem>
      )
  )

  const correctAnswerLabel = transformedChildren.find(
    child => child.props.value === correctAnswer
  )?.props?.children

  // Determine the correct value based on badgeClass or the correct prop
  const getCorrectValue = () => {
    if (badgeClass?.variant === Variant.correct) return true
    if (badgeClass?.variant === Variant.invalid) return false
    if (badgeClass?.variant === Variant.neutral || badgeClass?.variant === null)
      return null
    return correct
  }

  const correctValue = getCorrectValue()

  return (
    <StyledReviewBadge
      as={groupMode ? TooltipFeedbackGroup : TooltipFeedback}
      correct={correctValue}
      correctAnswer={correctAnswerLabel}
      groupMode={groupMode}
      items={feedbackItems}
      anchorOrigin={REVIEW_BADGE_ORIGIN.topLeft}
    >
      <StyledSelect
        inline
        variant="outlined"
        value={value}
        onChange={handleChange}
        isLesson={isLesson}
        badgeVariant={badgeClass?.variant}
        questionCompleted={questionCompleted}
        {...selectProps}
        renderValue={renderSelectValue(childrenArr)}
        disabled={questionCompleted}
      >
        {transformedChildren}
      </StyledSelect>
    </StyledReviewBadge>
  )
}
